import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = 'https://customer-assets.emergentagent.com/job_truck-hub-2/artifacts/ve3g49ci_truck2.jpg';

const CarriersSection = () => {
  const [images, setImages] = useState([]);   // array of { src, caption }
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [galleryRes, compRes] = await Promise.all([
        axios.get(`${API}/gallery`).catch(() => ({ data: [] })),
        axios.get(`${API}/competition/photos`).catch(() => ({ data: { photos: [] } })),
      ]);
      const gallery = (galleryRes.data || []).map((g) => ({
        src: g.image_url,
        caption: g.caption || null,
      }));
      const comp = (compRes.data?.photos || []).map((p) => ({
        src: `${API}/competition/photos/${p.id}/image`,
        caption: p.company_name || p.submitter_name || null,
      }));
      // Interleave so the rotation feels mixed rather than blocks of one source
      const combined = [];
      const max = Math.max(gallery.length, comp.length);
      for (let i = 0; i < max; i++) {
        if (comp[i]) combined.push(comp[i]);
        if (gallery[i]) combined.push(gallery[i]);
      }
      if (!cancelled && combined.length > 0) setImages(combined);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (images.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 4500);
    return () => clearInterval(t);
  }, [images.length]);

  const current = images[idx];
  const src = current?.src || FALLBACK;
  const caption = current?.caption;

  return (
    <div className="bg-gray-50 py-12 sm:py-16 md:py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="relative order-2 md:order-1">
            <div className="relative rounded-lg overflow-hidden shadow-lg aspect-[4/3] bg-slate-100">
              {images.length > 0 ? (
                images.map((img, i) => (
                  <img
                    key={img.src + i}
                    src={img.src}
                    alt={img.caption || `Truckn Send It community photo ${i + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
                    loading="lazy"
                    data-testid={`carriers-hero-photo-${i}`}
                  />
                ))
              ) : (
                <img
                  src={FALLBACK}
                  alt="Truckn Send It Transport"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              {caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-4 py-3">
                  <p className="text-white text-xs sm:text-sm font-medium">{caption}</p>
                </div>
              )}
              {images.length > 1 && (
                <div className="absolute bottom-3 right-3 flex gap-1.5">
                  {images.slice(0, Math.min(images.length, 8)).map((_, i) => (
                    <span
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx % 8 ? 'bg-white w-4' : 'bg-white/60'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="order-1 md:order-2">
            <h3 className="text-xs sm:text-sm font-semibold text-red-600 mb-2">For carriers</h3>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4 sm:mb-6">
              Peace of Mind
            </h2>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-6">
              Our team is made up of 100% Australian transport industry professionals, from drivers, ops managers and compliance staff; we&apos;ve experienced the real world issues first hand, so we&apos;re here to ask the questions, gather all the information so you don&apos;t waste your time and money.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarriersSection;
